/** @file Walks a PNG's chunks from IHDR to IEND, checking each CRC; keeps a few facts, O(1) per chunk. */
import { crc32 } from './crc32';
import { pngInvalid } from './pngInvalid';

/** What the decode needs from the chunks: scalars and at most two views into the file (nothing is copied). */
export interface PngChunks {
  /** Offset of the first IDAT chunk (its length field), -1 when there is none; the inflate walks the rest lazily. */
  firstIdat: number;
  idatCount: number;
  /** The first PLTE before any IDAT, and how many PLTE came before IDAT (a later PLTE is out of place, ignored). */
  plte: Uint8Array | undefined;
  plteCount: number;
  /**
   * The tRNS that applies, chosen as libpng does: the first valid one before any IDAT (grey: 2 bytes, RGB: 6 bytes,
   * palette: 1..min(entries, 2^depth) bytes after its PLTE; none for grey+alpha or RGBA). Late, duplicate and invalid\n   * ones are ignored.
   */
  trns: Uint8Array | undefined;
}

const IDAT = 0x49444154;
const IEND = 0x49454e44;
const PLTE = 0x504c5445;
const TRNS = 0x74524e53;

/** A chunk type for a hint: four ASCII letters, else a neutral word (built only when a hint needs it). */
function nameOf(type: number): string {
  const name = String.fromCharCode(type >>> 24, (type >>> 16) & 255, (type >>> 8) & 255, type & 255);
  return /^[A-Za-z]{4}$/.test(name) ? name : 'unnamed';
}

/** Payload offsets of the chunks that matter (-1: none seen) and the counts; filled by one forward pass. */
interface Seen {
  colorType: number;
  bitDepth: number;
  firstIdat: number;
  idatCount: number;
  plte: number;
  plteLength: number;
  plteCount: number;
  trns: number;
  trnsLength: number;
}

/** Whether a tRNS of `size` bytes is valid where it stands (libpng's rules), given what came before it. */
function trnsValid(seen: Seen, size: number): boolean {
  if (seen.trns >= 0 || seen.idatCount > 0) return false;
  if (seen.colorType === 0 || seen.colorType === 2) return size === (seen.colorType === 0 ? 2 : 6);
  // libpng truncates the palette to 2^depth entries, so a longer tRNS is invalid even when the PLTE holds more.
  return seen.colorType === 3 && seen.plte >= 0 && size > 0 && size <= Math.min(256, 1 << seen.bitDepth, seen.plteLength / 3);
}

/** Records the chunk at `at` (its length field) of `type` and payload `size`; integers only. */
function record(seen: Seen, at: number, type: number, size: number): void {
  if (type === IDAT) {
    if (seen.firstIdat < 0) seen.firstIdat = at;
    seen.idatCount++;
  } else if (type === PLTE && seen.idatCount === 0 && seen.plteCount++ === 0) {
    seen.plte = at + 8;
    seen.plteLength = size;
  } else if (type === TRNS && trnsValid(seen, size)) {
    seen.trns = at + 8;
    seen.trnsLength = size;
  }
}

/**
 * Chunks after the IHDR (bytes 8..33, read by readPngIhdr), each CRC checked as it passes; VALIDATION at `path` for a
 * cut, damaged or endless file. Keeps integers per chunk kind, never a value per chunk, so a file of millions of tiny
 * chunks costs the same memory as one chunk.
 */
export function readPngChunks(bytes: Uint8Array, path: string, header: { colorType: number; bitDepth: number }): PngChunks {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const seen: Seen = { colorType: header.colorType, bitDepth: header.bitDepth, firstIdat: -1, idatCount: 0, plte: -1, plteLength: 0, plteCount: 0, trns: -1, trnsLength: 0 };
  for (let at = 33; ; ) {
    if (at + 12 > bytes.length) return pngInvalid(path, 'the PNG ends before its IEND chunk (truncated file)');
    const size = view.getUint32(at);
    const type = view.getUint32(at + 4);
    if (size > bytes.length - at - 12) return pngInvalid(path, `the PNG ends inside its ${nameOf(type)} chunk (truncated file)`);
    if (view.getUint32(at + 8 + size) !== crc32(bytes, at + 4, at + 8 + size)) return pngInvalid(path, `the PNG's ${nameOf(type)} chunk is damaged (CRC mismatch)`);
    if (type === IEND) break;
    record(seen, at, type, size);
    at += 12 + size;
  }
  const view8 = (at: number, length: number): Uint8Array | undefined => (at < 0 ? undefined : bytes.subarray(at, at + length));
  return { firstIdat: seen.firstIdat, idatCount: seen.idatCount, plte: view8(seen.plte, seen.plteLength), plteCount: seen.plteCount, trns: view8(seen.trns, seen.trnsLength) };
}
